import React, { Component } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import DashboardHeader from '@/components/Header/DashboardHeader';
import { Footer } from '@/components/organisms/Footer';
import { apiService } from '@/services/api';
import { tournamentWebSocketService } from '@/services/websocket';
import TournamentBracket from '@/components/TournamentBracket/TournamentBracket';
import { TournamentChat } from '@/components/TournamentChat';
import { TournamentGroupChat } from '@/components/TournamentGroupChat';
import { tournamentChatWebSocketService } from '@/services/tournamentChatWebSocket';
import styles from './TournamentLive.module.scss';

// Default avatar for players without profile pictures
const DEFAULT_AVATAR =
  'https://www.gravatar.com/avatar/00000000000000000000000000000000?d=mp&f=y&s=400';

const isValidAvatar = (u?: any) =>
  typeof u === 'string' && u.trim() !== '' && !/^null|undefined$/i.test(String(u));

// Resolve an avatar from common fields or return the default
const getAvatarFromPlayer = (p?: any) => {
  const u =
    p?.profilePic ??
    p?.avatar ??
    p?.photoUrl ??
    p?.imageUrl ??
    p?.picture ??
    '';
  return isValidAvatar(u) ? String(u) : DEFAULT_AVATAR;
};

interface TournamentLiveState {
  loading: boolean;
  error?: string;
  tournament: any;
  tournamentStatus: any;
  players: any[];
  selectedRound: number | null;
  selectedRoundTab: 'players' | 'winners' | 'losers';
  mainTab: 'registered-players' | 'rounds' | 'results' | 'bracket';
  expandedRounds: Set<number>; // Track which rounds are expanded
  showChatPanel: boolean;
  showGroupChatPanel: boolean;
  isCustomerRegistered?: boolean;
  /** Unread count for tournament chat (when chat panel is closed). Updated via socket and list API. */
  tournamentChatUnreadCount: number;
}

// Higher-order component to provide navigation and params
function withNavigation(WrappedComponent: any) {
  return function WithNavigationComponent(props: any) {
    const navigate = useNavigate();
    const params = useParams();
    return <WrappedComponent {...props} navigate={navigate} params={params} />;
  };
}

class TournamentLive extends Component<any, TournamentLiveState> {
  private refreshInterval: NodeJS.Timeout | null = null;
  private tournamentId: string | null = null;
  private websocketConnected: boolean = false;
  
  private getPrefsKey(tournamentId: string) {
    return `tournamentLive:prefs:${tournamentId}`;
  }
  private loadViewPrefs(tournamentId: string): { mainTab?: TournamentLiveState['mainTab']; selectedRound?: number | null } {
    try {
      const raw = sessionStorage.getItem(this.getPrefsKey(tournamentId));
      if (!raw) return {};
      return JSON.parse(raw) || {};
    } catch {
      return {};
    }
  }
  private saveViewPrefs(tournamentId: string, prefs: Partial<{ mainTab: TournamentLiveState['mainTab']; selectedRound: number | null }>) {
    try {
      const existing = this.loadViewPrefs(tournamentId);
      const merged = { ...existing, ...prefs };
      sessionStorage.setItem(this.getPrefsKey(tournamentId), JSON.stringify(merged));
    } catch {}
  }

  constructor(props: any) {
    super(props);
    this.state = {
      loading: true,
      tournament: null,
      tournamentStatus: null,
      players: [],
      selectedRound: null,
      selectedRoundTab: 'players',
      mainTab: 'registered-players',
      expandedRounds: new Set(),
      showChatPanel: false,
      showGroupChatPanel: false,
      isCustomerRegistered: undefined,
      tournamentChatUnreadCount: 0
    };
  }

  async componentDidMount() {
    const tournamentId = this.props.params.tournamentId as string;
    if (tournamentId) {
      this.tournamentId = tournamentId;
      
      // Restore last viewed tab/round
      const prefs = this.loadViewPrefs(tournamentId);
      const urlOpenChat = typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('openChat') : null;
      this.setState((prev) => ({
        mainTab: (prefs.mainTab as TournamentLiveState['mainTab']) || prev.mainTab,
        selectedRound: typeof prefs.selectedRound !== 'undefined' ? prefs.selectedRound! : prev.selectedRound,
        showChatPanel: !!urlOpenChat || prev.showChatPanel
      }));

      await this.loadTournamentStatus(tournamentId);
      
      // Setup WebSocket connection for real-time updates
      this.setupWebSocket(tournamentId);
      
      // Tournament chat: connect for notifications (user room) and load initial unread count
      this.setupTournamentChatNotifications(tournamentId);
      
      // Auto-refresh every 60 seconds as fallback (reduced frequency when WebSocket is available)
      // If WebSocket is connected, polling is just a safety net
      this.refreshInterval = setInterval(() => {
        // Only poll if WebSocket is not connected
        if (!this.websocketConnected) {
          this.loadTournamentStatus(tournamentId);
        }
      }, 60000);
    }
  }

  setupWebSocket = (tournamentId: string) => {
    try {
      // Connect to WebSocket and join tournament room
      tournamentWebSocketService.connect(tournamentId);
      
      // Check connection status
      this.websocketConnected = tournamentWebSocketService.isConnected();
      
      // Setup event handlers
      tournamentWebSocketService.on('player:registered', this.handlePlayerRegistered);
      tournamentWebSocketService.on('match:update', this.handleMatchUpdate);
      tournamentWebSocketService.on('round:update', this.handleRoundUpdate);
      tournamentWebSocketService.on('tournament:status', this.handleTournamentStatus);
      tournamentWebSocketService.on('error', this.handleWebSocketError);
      
      // Monitor connection status
      const checkConnection = setInterval(() => {
        const connected = tournamentWebSocketService.isConnected();
        if (connected !== this.websocketConnected) {
          this.websocketConnected = connected;
          if (connected) {
            console.log('[TournamentLive] WebSocket connected, reducing polling frequency');
          } else {
            console.log('[TournamentLive] WebSocket disconnected, using polling fallback');
          }
        }
      }, 5000);
      
      // Store interval for cleanup
      (this as any).connectionCheckInterval = checkConnection;
      
    } catch (error) {
      console.error('[TournamentLive] Failed to setup WebSocket:', error);
      // Continue with polling fallback
    }
  }

  handlePlayerRegistered = (data: any) => {
    console.log('[TournamentLive] Player registered:', data);
    // Refresh tournament status to get updated player list
    if (this.tournamentId) {
      this.loadTournamentStatus(this.tournamentId);
    }
  }

  handleMatchUpdate = (data: any) => {
    console.log('[TournamentLive] Match updated:', data);
    
    if (data.deleted) {
      // Match was deleted - refresh to get updated list
      if (this.tournamentId) {
        this.loadTournamentStatus(this.tournamentId);
      }
    } else {
      // Update match in current state
      this.setState((prevState) => {
        const tournamentStatus = prevState.tournamentStatus;
        if (!tournamentStatus || !tournamentStatus.rounds) {
          return null;
        }

        const updatedRounds = tournamentStatus.rounds.map((round: any) => {
          if (round.round_id === data.roundId || round.id === data.roundId) {
            const updatedMatches = (round.matches || []).map((match: any) => {
              if (match.match_id === data.matchId || match.id === data.matchId) {
                return {
                  ...match,
                  status: data.status,
                  winner: data.winnerId ? { id: data.winnerId } : match.winner,
                  score: data.scorePlayer1 !== undefined && data.scorePlayer2 !== undefined
                    ? `${data.scorePlayer1}-${data.scorePlayer2}`
                    : match.score,
                  start_time: data.startTime || match.start_time,
                  end_time: data.endTime || match.end_time,
                  table_number: data.tableNumber !== undefined ? data.tableNumber : match.table_number
                };
              }
              return match;
            });

            // Add new match if it doesn't exist
            const matchExists = updatedMatches.some((m: any) => 
              (m.match_id === data.matchId || m.id === data.matchId)
            );
            if (!matchExists && data.status !== 'deleted') {
              updatedMatches.push({
                match_id: data.matchId,
                id: data.matchId,
                match_number: data.matchNumber,
                status: data.status,
                player1: data.player1Id ? { id: data.player1Id } : null,
                player2: data.player2Id ? { id: data.player2Id } : null,
                winner: data.winnerId ? { id: data.winnerId } : null,
                score: data.scorePlayer1 !== undefined && data.scorePlayer2 !== undefined
                  ? `${data.scorePlayer1}-${data.scorePlayer2}`
                  : null,
                start_time: data.startTime,
                end_time: data.endTime,
                table_number: data.tableNumber
              });
            }

            return {
              ...round,
              matches: updatedMatches
            };
          }
          return round;
        });

        return {
          tournamentStatus: {
            ...tournamentStatus,
            rounds: updatedRounds
          }
        };
      });
    }
  }

  handleRoundUpdate = (data: any) => {
    console.log('[TournamentLive] Round updated:', data);
    
    // Update round in current state
    this.setState((prevState) => {
      const tournamentStatus = prevState.tournamentStatus;
      if (!tournamentStatus || !tournamentStatus.rounds) {
        return null;
      }

      const updatedRounds = tournamentStatus.rounds.map((round: any) => {
        if (round.round_id === data.roundId || round.id === data.roundId || 
            round.round_number === data.roundNumber) {
          return {
            ...round,
            status: data.status,
            display_name: data.roundDisplayName || data.roundName || round.display_name,
            is_freezed: data.isFreezed !== undefined ? data.isFreezed : round.is_freezed
          };
        }
        return round;
      });

      return {
        tournamentStatus: {
          ...tournamentStatus,
          rounds: updatedRounds
        }
      };
    });
  }

  handleTournamentStatus = (data: any) => {
    console.log('[TournamentLive] Tournament status changed:', data);
    
    // Update tournament status
    this.setState((prevState) => {
      if (prevState.tournament) {
        return {
          tournament: {
            ...prevState.tournament,
            status: data.status,
            started_at: data.startedAt || prevState.tournament.started_at,
            ended_at: data.endedAt || prevState.tournament.ended_at
          }
        };
      }
      return null;
    });
    
    // Refresh full status to get all updates
    if (this.tournamentId) {
      this.loadTournamentStatus(this.tournamentId);
    }
  }

  handleWebSocketError = (error: any) => {
    console.error('[TournamentLive] WebSocket error:', error);
    // Continue with polling fallback
    this.websocketConnected = false;
  }

  componentDidUpdate(prevProps: any, prevState: TournamentLiveState) {
    // Auto-select last round if rounds exist but none is selected
    const rounds = this.state.tournamentStatus?.rounds || [];
    if (rounds.length > 0 && !this.state.selectedRound && prevState.tournamentStatus?.rounds?.length === 0) {
      console.log('[TournamentLive] Auto-selecting last round in componentDidUpdate');
      const lastRound = rounds[rounds.length - 1];
      const lastRoundNumber = lastRound.round_number || lastRound.roundNumber;
      if (lastRoundNumber) {
        this.setState({ selectedRound: lastRoundNumber });
      }
    }
  }

  componentWillUnmount() {
    // Clear polling interval
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }
    
    // Clear connection check interval
    if ((this as any).connectionCheckInterval) {
      clearInterval((this as any).connectionCheckInterval);
    }
    
    // Disconnect WebSocket
    tournamentWebSocketService.off('player:registered', this.handlePlayerRegistered);
    tournamentWebSocketService.off('match:update', this.handleMatchUpdate);
    tournamentWebSocketService.off('round:update', this.handleRoundUpdate);
    tournamentWebSocketService.off('tournament:status', this.handleTournamentStatus);
    tournamentWebSocketService.off('error', this.handleWebSocketError);
    tournamentWebSocketService.disconnect();

    // Tournament chat notifications
    tournamentChatWebSocketService.off('tournament_chat:notification', this.handleTournamentChatNotification);
    tournamentChatWebSocketService.disconnect();
  }

  /** Connect for tournament chat notifications (user room) and load initial unread. See docs/TOURNAMENT_CHAT_NOTIFICATIONS.md */
  setupTournamentChatNotifications = (tournamentId: string) => {
    tournamentChatWebSocketService.connect();
    tournamentChatWebSocketService.on('tournament_chat:notification', this.handleTournamentChatNotification);
    this.loadTournamentChatUnreadCount(tournamentId);
  };

  handleTournamentChatNotification = (data: { tournamentId?: string; unreadCount?: number }) => {
    if (this.tournamentId && data.tournamentId === this.tournamentId) {
      const count = typeof data.unreadCount === 'number' ? data.unreadCount : this.state.tournamentChatUnreadCount + 1;
      this.setState({ tournamentChatUnreadCount: count });
    }
  };

  loadTournamentChatUnreadCount = async (tournamentId: string) => {
    try {
      const res = await apiService.getTournamentChatConversations(tournamentId, { limit: 10 });
      const conversations = (res as any).conversations || [];
      const total = conversations.reduce((sum: number, c: any) => sum + (c.unreadCount || 0), 0);
      this.setState({ tournamentChatUnreadCount: total });
    } catch (_e) {
      // ignore
    }
  };

  loadTournamentStatus = async (tournamentId: string) => {
    try {
      console.log('[TournamentLive] Loading tournament status for:', tournamentId);
      const response = await apiService.getTournamentDetails(tournamentId);
      
      if (response.success && response.data) {
        const data = response.data;
        console.log('[TournamentLive] API Response:', data);
        
        // Extract tournament data - handle both nested and flat structures
        const tournament = data.tournament || data;
        
        // Extract tournament_status, players, and other data
        const tournamentStatus = data.tournament_status || null;
        const players = data.players || [];
        
        console.log('[TournamentLive] Tournament:', tournament);
        console.log('[TournamentLive] Tournament Status:', tournamentStatus);
        console.log('[TournamentLive] Players:', players);
        
        // Auto-select last round if available (only if not already selected)
        const lastRoundNumber = tournamentStatus && tournamentStatus.rounds && tournamentStatus.rounds.length > 0
          ? (() => {
              const lastRound = tournamentStatus.rounds[tournamentStatus.rounds.length - 1];
              return lastRound.round_number || lastRound.roundNumber || null;
            })()
          : null;

        console.log('[TournamentLive] Setting state with selectedRound (if empty):', lastRoundNumber);

        const isCustomerRegistered = tournament.is_customer_registered ?? data.is_customer_registered ?? undefined;
        this.setState({
          tournament: tournament,
          tournamentStatus: tournamentStatus,
          players: players,
          loading: false,
          error: undefined,
          selectedRound: this.state.selectedRound ?? (lastRoundNumber || null),
          selectedRoundTab: (this.state.selectedRound ?? lastRoundNumber) ? this.state.selectedRoundTab : 'players',
          mainTab: this.state.mainTab,
          expandedRounds: new Set(),
          isCustomerRegistered
        });

        // Persist current prefs
        this.saveViewPrefs(tournamentId, {
          mainTab: this.state.mainTab,
          selectedRound: this.state.selectedRound ?? lastRoundNumber ?? null
        });
      } else {
        throw new Error(response.message || 'Failed to load tournament');
      }
    } catch (error: any) {
      console.error('[TournamentLive] Failed to load tournament:', error);
      this.setState({
        loading: false,
        error: error?.message || 'Failed to load tournament status'
      });
    }
  };

  getRoundStatusColor = (status: string) => {
    if (!status) return '#6b7280';
    switch (status.toLowerCase()) {
      case 'completed': return '#22c55e';
      case 'active': return '#3b82f6';
      case 'ongoing': return '#ef4444';
      case 'in_progress': return '#ef4444';
      case 'pending': return '#f59e0b';
      case 'started': return '#3b82f6';
      case 'running': return '#ef4444';
      default: return '#6b7280';
    }
  };

  getMatchStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'completed': return '#22c55e';
      case 'ongoing': return '#ef4444';
      case 'in_progress': return '#ef4444';
      case 'scheduled': return '#f59e0b';
      default: return '#6b7280';
    }
  };

  formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'TBD';
    try {
      const date = new Date(dateString);
      return date.toLocaleString();
    } catch {
      return dateString;
    }
  };

  // Get winners from matches in a round
  getRoundWinners = (round: any) => {
    if (!round || !round.matches) return [];
    const winners: any[] = [];
    round.matches.forEach((match: any) => {
      if (match.winner && match.status === 'completed') {
        winners.push(match.winner);
      }
    });
    return winners;
  };

  // Get losers from matches in a round
  getRoundLosers = (round: any) => {
    if (!round || !round.matches) return [];
    const losers: any[] = [];
    round.matches.forEach((match: any) => {
      if (match.status === 'completed') {
        if (match.winner?.id === match.player1?.id && match.player2) {
          losers.push(match.player2);
        } else if (match.winner?.id === match.player2?.id && match.player1) {
          losers.push(match.player1);
        }
      }
    });
    return losers;
  };

  // Find player by ID in the players list
  findPlayerById = (playerId: string) => {
    return this.state.players.find((p: any) => 
      p.customerProfileId === playerId || 
      p.userId === playerId || 
      p.id === playerId
    );
  };

  // Try to find a player's avatar from available player data using common fields
  private findPlayerAvatar = (winner: any): string => {
    const candidates = this.state.players || [];
    const email = (winner?.email || winner?.userEmail || winner?.mail || '').toLowerCase();
    const name = (winner?.name || winner?.fullName || '').trim().toLowerCase();

    let found: any | undefined;
    if (email) {
      found = candidates.find((p: any) => (p.email || '').toLowerCase() === email);
    }
    if (!found && name) {
      found = candidates.find((p: any) => {
        const full = (p.fullName || `${p.firstName || ''} ${p.lastName || ''}` || '')
          .trim()
          .toLowerCase();
        return full && full === name;
      });
    }
    return getAvatarFromPlayer(found);
  };

  // Toggle round expansion
  toggleRoundExpansion = (roundNumber: number) => {
    this.setState((prevState) => {
      const expandedRounds = new Set(prevState.expandedRounds);
      if (expandedRounds.has(roundNumber)) {
        expandedRounds.delete(roundNumber);
      } else {
        expandedRounds.add(roundNumber);
      }
      return { expandedRounds };
    });
  };

  render() {
    try {
      const { loading, error, tournament, tournamentStatus, players } = this.state;

      // Debug logging
      console.log('[TournamentLive] Render - State:', {
        mainTab: this.state.mainTab,
        rounds: tournamentStatus?.rounds?.length || 0,
        players: players?.length || 0,
        expandedRounds: Array.from(this.state.expandedRounds || []),
        tournamentStatus: !!tournamentStatus,
        tournament: !!tournament,
        loading,
        error
      });

      if (loading) {
        return (
          <>
            <DashboardHeader />
            <div className={styles.tournamentLiveContainer}>
              <div className={styles.loadingContainer}>
                <div className={styles.loadingSpinner}></div>
                <p>Loading tournament status...</p>
              </div>
            </div>
            <Footer />
          </>
        );
      }

      if (error || !tournament) {
        return (
          <>
            <DashboardHeader />
            <div className={styles.tournamentLiveContainer}>
              <div className={styles.errorContainer}>
                <h2>Unable to Load Tournament</h2>
                <p>{error || 'Tournament not found'}</p>
                <button 
                  className={styles.backButton}
                  onClick={() => this.props.navigate('/customer/friends')}
                >
                  Back to Dashboard
                </button>
              </div>
            </div>
            <Footer />
          </>
        );
      }

    const rounds = tournamentStatus?.rounds || [];
    const winnersPodium = tournamentStatus?.winners_podium || [];

    console.log('[TournamentLive] Render - Full State:', {
      roundsCount: rounds.length,
      rounds: rounds,
      playersCount: players.length,
      players: players,
      mainTab: this.state.mainTab,
      expandedRounds: Array.from(this.state.expandedRounds),
      tournamentStatus,
      tournament
    });

    return (
      <>
        <DashboardHeader />
        <div className={styles.tournamentLiveContainer}>
          <div className={styles.tournamentLiveContent}>
            {/* Back Button */}
            <button 
              className={styles.backButton}
              onClick={() => this.props.navigate(-1)}
            >
              ← Back
            </button>

            {/* Tournament Header */}
            <div className={styles.tournamentHeader}>
              <div className={styles.tournamentTitleSection}>
                <h1 className={styles.tournamentTitle}>
                  {tournament.tournamentName || tournament.tournament_name || 'Tournament'}
                </h1>
                <div className={styles.tournamentMeta}>
                  <span className={styles.gameType}>
                    {tournament.gameType || tournament.game_type || '8-Ball'}
                  </span>
                  <span 
                    className={styles.statusBadge}
                    style={{ 
                      backgroundColor: this.getRoundStatusColor(
                        tournamentStatus?.tournament?.status || 
                        tournament.status || 
                        'upcoming'
                      )
                    }}
                  >
                    {(tournamentStatus?.tournament?.status || tournament.status || 'UPCOMING').toUpperCase()}
                  </span>
                  {(tournamentStatus?.tournament?.status === 'in_progress' || 
                    tournament.status === 'started' || 
                    tournament.status === 'running' || 
                    tournament.status === 'active') ? (
                    <span className={styles.liveIndicator}>
                      <span className={styles.liveDot}></span>
                      LIVE
                    </span>
                  ) : null}
                </div>
              </div>

              <div className={styles.tournamentStats}>
                <div className={styles.statItem}>
                  <span className={styles.statLabel}>Players</span>
                  <span className={styles.statValue}>
                    {tournamentStatus?.tournament?.registered || tournament.registeredCount || tournament.registered_count || players.length || 0}/
                    {tournamentStatus?.tournament?.max_players || tournament.maxPlayers || tournament.max_players || 0}
                  </span>
                </div>
                <div className={styles.statItem}>
                  <span className={styles.statLabel}>Prize Pool</span>
                  <span className={styles.statValue}>
                    ${tournament.prizePool || tournament.prize_pool || 0}
                  </span>
                </div>
                <div className={styles.statItem}>
                  <span className={styles.statLabel}>Entry Fee</span>
                  <span className={styles.statValue}>
                    ${tournament.entryFee || tournament.entry_fee || 0}
                  </span>
                </div>
                {tournament.tournamentDate && (
                  <div className={styles.statItem}>
                    <span className={styles.statLabel}>Date</span>
                    <span className={styles.statValue}>
                      {this.formatDate(tournament.tournamentDate)}
                    </span>
                  </div>
                )}
                {tournament.tournamentTime && (
                  <div className={styles.statItem}>
                    <span className={styles.statLabel}>Time</span>
                    <span className={styles.statValue}>
                      {tournament.tournamentTime}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Chat: 1-to-1 with organizer + Group chat - show when tournament is not completed/cancelled */}
            {(() => {
              const status = (tournamentStatus?.tournament?.status || tournament.status || '').toLowerCase();
              const chatDisabled = status === 'completed' || status === 'cancelled';
              const unreadCount = this.state.tournamentChatUnreadCount || 0;
              return (
                <div className={styles.chatWithOrganizerRow}>
                  <button
                    type="button"
                    className={styles.chatWithOrganizerButton}
                    onClick={() => this.setState({ showChatPanel: true, showGroupChatPanel: false })}
                    disabled={chatDisabled}
                    title={chatDisabled ? 'Chat is only available until the tournament ends' : 'Chat with organizer for questions and updates'}
                  >
                    💬 Chat with organizer
                    {unreadCount > 0 && (
                      <span className={styles.chatUnreadBadge} aria-label={`${unreadCount} unread`}>
                        {unreadCount > 99 ? '99+' : unreadCount}
                      </span>
                    )}
                  </button>
                  <button
                    type="button"
                    className={styles.groupChatButton}
                    onClick={() => this.setState({ showGroupChatPanel: true, showChatPanel: false })}
                    disabled={chatDisabled}
                    title={chatDisabled ? 'Group chat available until tournament ends' : 'Chat with all participants'}
                  >
                    👥 Group chat
                  </button>
                  {chatDisabled && (
                    <span className={styles.chatDisabledHint}>Chat available until tournament ends</span>
                  )}
                </div>
              );
            })()}

            {/* Main Tabs */}
            <div className={styles.mainTabs}>
              <button
                className={`${styles.mainTabButton} ${this.state.mainTab === 'registered-players' ? styles.active : ''}`}
                onClick={() => {
                  this.setState({ mainTab: 'registered-players' });
                  const tournamentId = this.props.params.tournamentId as string;
                  if (tournamentId) this.saveViewPrefs(tournamentId, { mainTab: 'registered-players' });
                }}
              >
                Registered Players ({players.length})
              </button>
              <button
                className={`${styles.mainTabButton} ${this.state.mainTab === 'rounds' ? styles.active : ''}`}
                onClick={() => {
                  this.setState({ mainTab: 'rounds' });
                  const tournamentId = this.props.params.tournamentId as string;
                  if (tournamentId) this.saveViewPrefs(tournamentId, { mainTab: 'rounds' });
                }}
              >
                Rounds ({rounds.length})
              </button>
              <button
                className={`${styles.mainTabButton} ${this.state.mainTab === 'results' ? styles.active : ''}`}
                onClick={() => {
                  this.setState({ mainTab: 'results' });
                  const tournamentId = this.props.params.tournamentId as string;
                  if (tournamentId) this.saveViewPrefs(tournamentId, { mainTab: 'results' });
                }}
              >
                Tournament Result
              </button>
              <button
                className={`${styles.mainTabButton} ${this.state.mainTab === 'bracket' ? styles.active : ''}`}
                onClick={() => {
                  this.setState({ mainTab: 'bracket' });
                  const tournamentId = this.props.params.tournamentId as string;
                  if (tournamentId) this.saveViewPrefs(tournamentId, { mainTab: 'bracket' });
                }}
              >
                Tournament Bracket
              </button>
            </div>

            {/* Registered Players Tab */}
            {this.state.mainTab === 'registered-players' && (
              <div className={styles.tabContent}>
                <div className={styles.registeredPlayersSection}>
                  <h2 className={styles.sectionTitle}>
                    Registered Players ({players.length})
                  </h2>
                  {players.length > 0 ? (
                    <div className={styles.playersGrid}>
                      {players.map((player: any, index: number) => (
                        <div key={player.customerProfileId || player.userId || player.id || index} className={styles.playerCard}>
                          <img
                            src={getAvatarFromPlayer(player)}
                            alt={player.fullName || player.firstName || 'Player'}
                            className={styles.playerAvatar}
                            onError={(e) => {
                              (e.currentTarget as HTMLImageElement).onerror = null;
                              (e.currentTarget as HTMLImageElement).src = DEFAULT_AVATAR;
                            }}
                          />
                          <div className={styles.playerInfo}>
                            <div className={styles.playerName}>
                              {player.fullName || `${player.firstName || ''} ${player.lastName || ''}`.trim() || 'Player'}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className={styles.noData}>
                      <p>No registered players yet</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Results Tab */}
            {this.state.mainTab === 'results' && (
              <div className={styles.tabContent}>
                <h2 className={styles.sectionTitle}>🏆 Tournament Result</h2>
                {winnersPodium.length > 0 ? (
                  <div className={styles.winnersPodium}>
                    <div className={styles.podiumGrid}>
                      {winnersPodium
                        .sort((a: any, b: any) => a.rank - b.rank)
                        .map((winner: any) => {
                          const rank = winner.rank;
                          const cls =
                            rank === 1 ? 'gold' :
                            rank === 2 ? 'silver' : 'bronze';
                          const title =
                            winner.title ||
                            (rank === 1 ? 'Champion' : rank === 2 ? 'Runner Up' : 'Finalist');
                          const avatar = this.findPlayerAvatar(winner);
                          const medal = rank === 1 ? '🥇' : rank === 2 ? '🥈' : '🥉';
                          return (
                            <div key={rank} className={`${styles.podiumItem} ${styles[cls]}`}>
                              <div className={`${styles.podiumRibbon} ${styles[cls]}`}>{title.toUpperCase()}</div>
                              <div className={styles.podiumAvatarWrap}>
                                <img
                                  src={avatar || DEFAULT_AVATAR}
                                  alt={winner.name || title}
                                  className={styles.podiumAvatar}
                                  onError={(e) => {
                                    (e.currentTarget as HTMLImageElement).onerror = null;
                                    (e.currentTarget as HTMLImageElement).src = DEFAULT_AVATAR;
                                  }}
                                />
                                <div className={`${styles.podiumMedal} ${styles[cls]}`}>{medal}</div>
                              </div>
                              <div className={styles.podiumRank}>#{rank}</div>
                              <div className={styles.podiumTitle}>{title}</div>
                              <div className={styles.podiumName}>{winner.name}</div>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                ) : (
                  <div className={styles.noData}>
                    <p>Final results are not available yet.</p>
                  </div>
                )}
              </div>
            )}

            {/* Tournament Bracket Tab */}
            {this.state.mainTab === 'bracket' && (
              <div className={styles.tabContent}>
                <TournamentBracket 
                  tournamentName={tournament.tournamentName || tournament.tournament_name || 'Tournament'}
                  rounds={rounds}
                  players={players}
                  tournamentStatus={tournamentStatus}
                />
              </div>
            )}

            {/* Rounds Tab */}
            {this.state.mainTab === 'rounds' && (
              <div className={styles.tabContent}>
                {rounds.length > 0 ? (
                  <>
                    {/* Round Tabs - Side by Side */}
                    <div className={styles.roundTabsContainer}>
                      {rounds.map((round: any, index: number) => {
                        const roundNumber = round.round_number || round.roundNumber || (index + 1);
                        const isActive = this.state.selectedRound === roundNumber;
                        return (
                          <button
                            key={roundNumber || index}
                            className={`${styles.roundTabButton} ${isActive ? styles.active : ''}`}
                            onClick={() => {
                              this.setState({ selectedRound: roundNumber });
                              const tournamentId = this.props.params.tournamentId as string;
                              if (tournamentId) this.saveViewPrefs(tournamentId, { selectedRound: roundNumber });
                            }}
                          >
                            <span className={styles.roundTabName}>
                              {round.display_name || round.displayName || `Round ${roundNumber}`}
                            </span>
                            <span 
                              className={styles.roundTabStatusBadge}
                              style={{ backgroundColor: this.getRoundStatusColor(round.status) }}
                            >
                              {round.status?.toUpperCase() || 'PENDING'}
                            </span>
                            <span className={styles.roundTabStats}>
                              {round.completed_matches || 0}/{round.total_matches || 0}
                            </span>
                          </button>
                        );
                      })}
                    </div>

                    {/* Selected Round Content */}
                    {this.state.selectedRound && (() => {
                      const selectedRoundData = rounds.find((r: any) => {
                        const rNum = r.round_number || r.roundNumber;
                        return rNum === this.state.selectedRound;
                      });
                      if (!selectedRoundData) {
                        // Auto-select last round if selected round not found
                        const lastRound = rounds[rounds.length - 1];
                        if (lastRound) {
                          const lastRoundNum = lastRound.round_number || lastRound.roundNumber || 1;
                          setTimeout(() => {
                            this.setState({ selectedRound: lastRoundNum });
                          }, 0);
                        }
                        return null;
                      }

                      const matches = selectedRoundData?.matches || [];
                      const roundNumber = selectedRoundData.round_number || selectedRoundData.roundNumber || 1;
                      
                      return (
                        <div className={styles.roundContent}>
                          <div className={styles.roundHeader}>
                            <h2 className={styles.roundTitle}>
                              {selectedRoundData.display_name || selectedRoundData.displayName || `Round ${roundNumber}`}
                            </h2>
                            <div className={styles.roundProgressInfo}>
                              <span>
                                {selectedRoundData.completed_matches || 0} / {selectedRoundData.total_matches || 0} matches completed
                              </span>
                              {selectedRoundData.status && (
                                <span 
                                  className={styles.roundStatus}
                                  style={{ color: this.getRoundStatusColor(selectedRoundData.status) }}
                                >
                                  {selectedRoundData.status.toUpperCase()}
                                </span>
                              )}
                            </div>
                          </div>
                          
                          {matches.length > 0 ? (
                            <div className={styles.matchesGrid}>
                              {matches.map((match: any, index: number) => {
                                    const player1Data = this.findPlayerById(match.player1?.id) || match.player1;
                                    const player2Data = this.findPlayerById(match.player2?.id) || match.player2;
                                    const isPlayer1Winner = match.winner?.id === match.player1?.id;
                                    const isPlayer2Winner = match.winner?.id === match.player2?.id;

                                    return (
                                      <div 
                                        key={match.match_number || index} 
                                        className={`${styles.matchCard} ${match.status === 'ongoing' || match.status === 'in_progress' ? styles.liveMatch : ''}`}
                                      >
                                        <div className={styles.matchHeader}>
                                          <span className={styles.matchNumber}>Match {match.match_number || index + 1}</span>
                                          {match.table_number && (
                                            <span className={styles.tableNumber}>Table #{match.table_number}</span>
                                          )}
                                          <span 
                                            className={styles.matchStatus}
                                            style={{ backgroundColor: this.getMatchStatusColor(match.status) }}
                                          >
                                            {match.status === 'completed' ? 'COMPLETED' : match.status?.toUpperCase() || 'SCHEDULED'}
                                          </span>
                                        </div>

                                        <div className={styles.matchPlayers}>
                                          <div className={`${styles.player} ${isPlayer1Winner ? styles.winner : ''}`}>
                                            <img
                                              src={getAvatarFromPlayer(player1Data)}
                                              alt={player1Data?.name || player1Data?.fullName || 'Player 1'}
                                              className={styles.playerMatchAvatar}
                                              onError={(e) => {
                                                (e.currentTarget as HTMLImageElement).onerror = null;
                                                (e.currentTarget as HTMLImageElement).src = DEFAULT_AVATAR;
                                              }}
                                            />
                                            <div className={styles.playerMatchInfo}>
                                              <div className={styles.playerName}>
                                                {match.player1?.name || player1Data?.fullName || player1Data?.firstName || 'TBD'}
                                                {isPlayer1Winner && <span className={styles.winnerIcon}>🏆</span>}
                                              </div>
                                              {player1Data?.skillLevel && (
                                                <div className={styles.playerSkill}>{player1Data.skillLevel}</div>
                                              )}
                                              {match.score && (
                                                <div className={styles.playerScore}>
                                                  {match.score.split('-')[0]?.trim() || '0'}
                                                </div>
                                              )}
                                            </div>
                                          </div>

                                          <div className={styles.vsDivider}>VS</div>

                                          <div className={`${styles.player} ${isPlayer2Winner ? styles.winner : ''}`}>
                                            <img
                                              src={getAvatarFromPlayer(player2Data)}
                                              alt={player2Data?.name || player2Data?.fullName || 'Player 2'}
                                              className={styles.playerMatchAvatar}
                                              onError={(e) => {
                                                (e.currentTarget as HTMLImageElement).onerror = null;
                                                (e.currentTarget as HTMLImageElement).src = DEFAULT_AVATAR;
                                              }}
                                            />
                                            <div className={styles.playerMatchInfo}>
                                              <div className={styles.playerName}>
                                                {match.player2?.name || player2Data?.fullName || player2Data?.firstName || 'TBD'}
                                                {isPlayer2Winner && <span className={styles.winnerIcon}>🏆</span>}
                                              </div>
                                              {player2Data?.skillLevel && (
                                                <div className={styles.playerSkill}>{player2Data.skillLevel}</div>
                                              )}
                                              {match.score && (
                                                <div className={styles.playerScore}>
                                                  {match.score.split('-')[1]?.trim() || '0'}
                                                </div>
                                              )}
                                            </div>
                                          </div>
                                        </div>

                                        {match.score && (
                                          <div className={styles.matchScore}>
                                            Final Score: {match.score}
                                          </div>
                                        )}

                                        <div className={styles.matchTiming}>
                                          {match.start_time && (
                                            <span>Started: {this.formatDate(match.start_time)}</span>
                                          )}
                                          {match.end_time && (
                                            <span>Ended: {this.formatDate(match.end_time)}</span>
                                          )}
                                          {match.start_time && !match.end_time && (match.status === 'ongoing' || match.status === 'in_progress') && (
                                            <span className={styles.liveTime}>In Progress</span>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })}
                            </div>
                          ) : (
                            <div className={styles.noData}>
                              <p>No matches in this round yet</p>
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </>
                ) : (
                  <div className={styles.noRounds}>
                    <h2 className={styles.sectionTitle}>Tournament Rounds</h2>
                    <div className={styles.infoCard}>
                      <p className={styles.infoMessage}>
                        Tournament rounds have not been created yet.
                      </p>
                      <p className={styles.noRoundsSubtext}>
                        {tournament.status === 'started' || tournament.status === 'running' 
                          ? 'Rounds will be available once the tournament organizer creates them.'
                          : 'The tournament will start soon.'}
                      </p>
                    </div>
                  </div>
                )}

                {/* Final Result / Winners Podium - Show at the end if tournament is completed */}
                {winnersPodium.length > 0 && (
                  <div className={styles.winnersPodium}>
                    <h2 className={styles.sectionTitle}>🏆 Final Result</h2>
                    <div className={styles.podiumGrid}>
                      {winnersPodium
                        .sort((a: any, b: any) => a.rank - b.rank)
                        .map((winner: any) => (
                          <div key={winner.rank} className={styles.podiumItem}>
                            <div className={styles.podiumRank}>#{winner.rank}</div>
                            <div className={styles.podiumTitle}>{winner.title}</div>
                            <div className={styles.podiumName}>{winner.name}</div>
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        {this.state.showChatPanel && this.tournamentId && tournament && (
          <TournamentChat
            tournamentId={this.tournamentId}
            tournamentName={tournament.tournamentName || tournament.tournament_name || 'Tournament'}
            organizerName={tournament.organizer?.displayName || tournament.organizerName || tournament.organizer?.name || 'Tournament Organizer'}
            onClose={() => this.setState({ showChatPanel: false })}
            onMarkedAsRead={() => this.setState({ tournamentChatUnreadCount: 0 })}
          />
        )}
        {this.state.showGroupChatPanel && this.tournamentId && tournament && (
          <TournamentGroupChat
            tournamentId={this.tournamentId}
            tournamentName={tournament.tournamentName || tournament.tournament_name || 'Tournament'}
            participantCount={players.length}
            onClose={() => this.setState({ showGroupChatPanel: false })}
          />
        )}
        <Footer />
      </>
    );
    } catch (renderError: any) {
      console.error('[TournamentLive] Render error:', renderError);
      return (
        <>
          <DashboardHeader />
          <div className={styles.tournamentLiveContainer}>
            <div className={styles.errorContainer}>
              <h2>Error Rendering Tournament</h2>
              <p>{renderError?.message || 'An error occurred while rendering the tournament page.'}</p>
              <button 
                className={styles.backButton}
                onClick={() => this.props.navigate('/customer/friends')}
              >
                Back to Dashboard
              </button>
            </div>
          </div>
          <Footer />
        </>
      );
    }
  }
}

export default withNavigation(TournamentLive);

